import React, { useCallback, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Sheet, SheetContent, SheetClose, SheetTitle } from 'App/shadcn/ui/sheet';
import T from 'Components/Helpers/translate';
import type { DelegationData } from './DelegationChip';

const DEFAULT_WIDTH = 640;
const MIN_WIDTH = 420;
// The chat content/composer max out at ~944px (workspaceStyles: ws-composer-wrap
// is calc(880px + 64px), ws-chat-body-inner is 880px). Widening past that just
// adds empty side gutters, so cap the drawer at the content width + a little
// padding rather than an arbitrary viewport fraction.
const MAX_WIDTH = 992;
// Below this usable width the drawer covers its containing block entirely
// instead of leaving the 48px gap (the floating chat window is 400px).
const FULL_COVER_BELOW = 480;
const WIDTH_KEY = 'delegationDrawerWidth';

const clampWidth = (w: number): number => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, w));

const readSavedWidth = (): number => {
	try {
		const saved = Number(window.localStorage.getItem(WIDTH_KEY));
		return saved > 0 ? clampWidth(saved) : DEFAULT_WIDTH;
	} catch {
		return DEFAULT_WIDTH;
	}
};

/**
 * WEB-6800: right-side drawer for a delegated (child) conversation, opened from a
 * DelegationChip in the parent conversation. The body is a real chat instance
 * (a nested WorkspaceChat for the child conversation, passed as `children`) so
 * the delegated work renders with the exact same affordances — markdown, tables,
 * cards, live updates — as the main chat, rather than a separate read-only list.
 *
 * The body wrapper carries the `ai-ws` class so the workspace's scoped styles
 * (`.ai-ws .ws-*` descendant rules) apply: the drawer renders through a portal
 * that lives outside the page's `.ai-ws` root, so without this the embedded
 * chat (composer border, scrollbars, prose) would render unstyled. The `--ws-*`
 * vars themselves already reach the portal via ShadcnScope's [data-ai-tokens]
 * container (WEB-8001). The grid/height layout `.ai-ws` normally sets is
 * overridden inline to a flex column so the thread scrolls and the composer
 * pins to the bottom inside the sheet.
 *
 * The left edge is draggable to resize the drawer (between MIN_WIDTH and MAX_WIDTH);
 * the chosen width is remembered in localStorage.
 */
const DelegationDrawer = ({
	delegation,
	onClose,
	onOpenFull,
	children
}: {
	delegation: DelegationData | null;
	onClose: () => void;
	onOpenFull?: (conversationId: number) => void;
	children?: React.ReactNode;
}) => {
	const title = delegation?.title || delegation?.agentName || T('assistant.delegatedWork') || 'Delegated work';
	const [width, setWidth] = useState<number>(readSavedWidth);
	const latestWidthRef = useRef(width);

	const startResize = useCallback((e: React.PointerEvent) => {
		e.preventDefault();
		const onMove = (ev: PointerEvent) => {
			// Never wider than the content cap, and never past the viewport edge.
			const max = Math.min(MAX_WIDTH, window.innerWidth - 48);
			const next = Math.min(max, Math.max(MIN_WIDTH, window.innerWidth - ev.clientX));
			latestWidthRef.current = next;
			setWidth(next);
		};
		const onUp = () => {
			window.removeEventListener('pointermove', onMove);
			window.removeEventListener('pointerup', onUp);
			document.body.style.userSelect = '';
			document.body.style.cursor = '';
			try {
				window.localStorage.setItem(WIDTH_KEY, String(Math.round(latestWidthRef.current)));
			} catch {
				/* non-fatal — width just won't persist */
			}
		};
		document.body.style.userSelect = 'none';
		document.body.style.cursor = 'ew-resize';
		window.addEventListener('pointermove', onMove);
		window.addEventListener('pointerup', onUp);
	}, []);

	return (
		<Sheet
			open={delegation != null}
			// modal={false} disables Radix's react-remove-scroll body lock. That lock
			// adds a document-level wheel handler that cancels wheel events it doesn't
			// recognise as targeting a scrollable area — and it can't see the chat
			// scroller across the shadow-DOM portal boundary, so it was eating mouse
			// wheel scroll inside the drawer (drag-scrollbar still worked). We still
			// render the dim overlay and close on outside-click/escape below.
			modal={false}
			onOpenChange={open => {
				if (!open) onClose();
			}}
		>
			<SheetContent
				side="right"
				hideClose
				className="flex flex-col p-0"
				// %-units, not vw: inside the floating chat window a transformed ancestor is
				// the fixed-position containing block, so vw would overflow the 400px window.
				style={{
					width: `${width}px`,
					maxWidth: `min(100%, max(calc(100% - 48px), ${FULL_COVER_BELOW}px))`
				}}
			>
				{/* Drag handle on the left edge to resize the drawer. */}
				<div
					onPointerDown={startResize}
					className="group absolute inset-y-0 left-0 z-20 flex w-2 cursor-ew-resize items-center justify-center"
					style={{ touchAction: 'none' }}
					role="separator"
					aria-orientation="vertical"
					aria-label={T('default.resize') || 'Resize'}
				>
					<span className="h-full w-px bg-transparent transition-colors group-hover:bg-border" />
				</div>

				<div className="flex items-center gap-3 border-b py-4 pl-4 pr-6">
					<SheetClose className="shrink-0 rounded-md p-2 text-grey-10 transition-colors hover:bg-secondary hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
						<X className="h-7 w-7" />
						<span className="sr-only">{T('default.close') || 'Close'}</span>
					</SheetClose>
					<div className="flex min-w-0 flex-col gap-1">
						<SheetTitle className="truncate">{title}</SheetTitle>
						{delegation != null && onOpenFull && (
							<button
								type="button"
								className="self-start text-body-sm text-grey-10 underline-offset-2 hover:text-foreground hover:underline"
								onClick={() => onOpenFull(delegation.conversationId)}
							>
								{T('assistant.openFullView') || 'Open full view'}
							</button>
						)}
					</div>
				</div>
				<div
					className="ai-ws ws-embedded-drawer"
					style={{
						display: 'flex',
						flexDirection: 'column',
						flex: '1 1 auto',
						minHeight: 0,
						height: 'auto',
						width: '100%',
						gridTemplateColumns: 'none'
					}}
				>
					{delegation != null && children}
				</div>
			</SheetContent>
		</Sheet>
	);
};

export default DelegationDrawer;
